import { useAuth } from "@clerk/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiUrl } from "@/lib/apiBase";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ProgramRegister {
  id: number;
  userId: string;
  schoolId: number | null;
  classId: number | null;
  title: string;
  subject: string | null;
  yearGroup: string | null;
  term: string | null;
  schoolYear: string | null;
  description: string | null;
  sourceFileObjectKey: string | null;
  sourceFileName: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProgramItem {
  id: number;
  programRegisterId: number;
  userId: string;
  schoolId: number | null;
  plannedOrder: number;
  plannedWeek: string | null;
  plannedLessonLabel: string | null;
  title: string;
  plannedContent: string | null;
  outcomeOrContentDotPoint: string | null;
  plannedResources: string | null;
  plannedAssessmentCheckpoint: string | null;
  overallStatus: string;
  teacherNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProgramDeliveryEvent {
  id: number;
  programRegisterId: number;
  programItemId: number;
  userId: string;
  schoolId: number | null;
  classId: number | null;
  sourceType: string;
  diaryEntryId: number | null;
  deliveryDate: string;
  period: string | null;
  manualSessionLabel: string | null;
  deliveryStatus: string;
  actualContentDelivered: string | null;
  modificationTags: string[] | null;
  differentiationSupportTypes: string[] | null;
  reflectionNote: string | null;
  remainingContentNote: string | null;
  aiDraftUsed: boolean;
  teacherApproved: boolean;
  createdAt: string;
  updatedAt: string;
  className?: string | null;
}

export interface ProgramEvaluation {
  id: number;
  programRegisterId: number;
  overallReflection: string | null;
  whatWorkedWell: string | null;
  whatChangedFromPlannedProgram: string | null;
  differentiationSupportSummary: string | null;
  suggestedUpdatesForNextTime: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RegisterDetail {
  register: ProgramRegister;
  items: ProgramItem[];
  events: ProgramDeliveryEvent[];
  evaluation: ProgramEvaluation | null;
}

// ── Status labels ─────────────────────────────────────────────────────────────

export const DELIVERY_STATUS_LABELS: Record<string, string> = {
  delivered_as_planned: "Completed as planned",
  partially_delivered: "Partially completed",
  modified_during_delivery: "Modified during delivery",
  not_delivered_due_to_interruption: "Not delivered / interrupted",
  postponed: "Postponed",
  replaced_with_alternate_content: "Replaced with alternate content",
  continued_from_previous_lesson: "Continued from previous lesson",
  completed_from_previous_lesson: "Completed from previous lesson",
};

export const DELIVERY_STATUSES = Object.keys(DELIVERY_STATUS_LABELS) as Array<keyof typeof DELIVERY_STATUS_LABELS>;

export function reflectionRequired(status: string): boolean {
  return status !== "delivered_as_planned";
}

export const SUPPORT_TYPES = [
  "Explicit instruction",
  "Chunked task",
  "Additional modelling",
  "Reduced literacy load",
  "Scaffolded worksheet",
  "Small group support",
  "Movement break",
  "Visual prompts",
  "Adjusted assessment conditions",
  "Modified pace/timing",
  "Alternate resource",
  "Additional guided practice",
  "Extension provided",
  "Practical demonstration",
  "Verbal instruction",
  "Teacher check-in",
  "Modified resource",
  "Repeated instruction",
];

export function statusBadgeClass(status: string): string {
  switch (status) {
    case "delivered_as_planned":
      return "bg-green-100 text-green-700 border-green-200";
    case "partially_delivered":
      return "bg-amber-100 text-amber-700 border-amber-200";
    case "modified_during_delivery":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "not_delivered_due_to_interruption":
      return "bg-red-100 text-red-700 border-red-200";
    case "postponed":
      return "bg-gray-100 text-gray-600 border-gray-200";
    case "replaced_with_alternate_content":
      return "bg-purple-100 text-purple-700 border-purple-200";
    case "continued_from_previous_lesson":
      return "bg-teal-100 text-teal-700 border-teal-200";
    case "completed_from_previous_lesson":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    default:
      return "bg-gray-100 text-gray-500 border-gray-200";
  }
}

// ── Auth fetch ────────────────────────────────────────────────────────────────

export function useProgramApi() {
  const { getToken } = useAuth();

  async function apiFetch<T = unknown>(path: string, init?: RequestInit): Promise<T> {
    const token = await getToken();
    const res = await fetch(apiUrl(path), {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token ?? ""}`,
        "X-App-Origin": window.location.hostname,
        ...(init?.headers ?? {}),
      },
    });
    if (!res.ok) {
      const data = await res.json().catch(() => null);
      const err = Object.assign(
        new Error((data as { error?: string } | null)?.error ?? res.statusText),
        { status: res.status, data }
      );
      throw err;
    }
    return res.json() as T;
  }

  return apiFetch;
}

// ── Query keys ────────────────────────────────────────────────────────────────

export const programRegistersKey = () => ["program-registers"] as const;
export const programRegisterDetailKey = (id: number) =>
  ["program-registers", id] as const;

// ── Hooks ─────────────────────────────────────────────────────────────────────

export function useProgramRegisters() {
  const apiFetch = useProgramApi();
  const { isLoaded } = useAuth();
  return useQuery({
    queryKey: programRegistersKey(),
    queryFn: () =>
      apiFetch<{ registers: ProgramRegister[] }>("/api/program-registers"),
    select: (data) => data.registers,
    // Do not fire until Clerk has resolved the session. The global QueryClient
    // has `retry: false`, so an early 401 (empty Bearer token before Clerk
    // initialises) would permanently land the query in the error state. Gating
    // on `isLoaded` guarantees the first attempt always carries a valid JWT.
    enabled: isLoaded,
  });
}

// Fetch all registers including archived — lazy, only enabled when the
// "Past programs" dialog is open.
export const archivedProgramRegistersKey = () =>
  ["program-registers", "includeArchived"] as const;

export function useArchivedProgramRegisters(options: { enabled: boolean }) {
  const apiFetch = useProgramApi();
  const { isLoaded } = useAuth();
  return useQuery({
    queryKey: archivedProgramRegistersKey(),
    queryFn: () =>
      apiFetch<{ registers: ProgramRegister[] }>(
        "/api/program-registers?includeArchived=1"
      ),
    select: (data) => data.registers.filter((r) => r.status === "archived"),
    enabled: isLoaded && options.enabled,
  });
}

export function useRestoreRegister() {
  const apiFetch = useProgramApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ register: ProgramRegister }>(
        `/api/program-registers/${id}/restore`,
        { method: "PATCH" }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: programRegistersKey() });
      qc.invalidateQueries({ queryKey: archivedProgramRegistersKey() });
    },
  });
}

export function useProgramRegisterDetail(id: number) {
  const apiFetch = useProgramApi();
  const { isLoaded } = useAuth();
  return useQuery({
    queryKey: programRegisterDetailKey(id),
    queryFn: () =>
      apiFetch<RegisterDetail>(`/api/program-registers/${id}`),
    // Same guard as useProgramRegisters: do not fire until Clerk has resolved
    // the session. With retry:false on the global QueryClient an early 401
    // before Clerk initialises would permanently land the query in error state.
    enabled: isLoaded && !isNaN(id) && id > 0,
  });
}

export function useCreateRegister() {
  const apiFetch = useProgramApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      title: string;
      subject?: string | null;
      yearGroup?: string | null;
      term?: string | null;
      schoolYear?: string | null;
      description?: string | null;
    }) =>
      apiFetch<{ register: ProgramRegister }>("/api/program-registers", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: programRegistersKey() }),
  });
}

export function useUpdateRegister(id: number) {
  const apiFetch = useProgramApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Partial<{
        title: string;
        subject: string | null;
        yearGroup: string | null;
        term: string | null;
        schoolYear: string | null;
        description: string | null;
      }>
    ) =>
      apiFetch<{ register: ProgramRegister }>(
        `/api/program-registers/${id}`,
        { method: "PUT", body: JSON.stringify(data) }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: programRegistersKey() });
      qc.invalidateQueries({ queryKey: programRegisterDetailKey(id) });
    },
  });
}

export function useDeleteRegister() {
  const apiFetch = useProgramApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ ok: boolean }>(`/api/program-registers/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: programRegistersKey() }),
  });
}

export function useCreateItem(registerId: number) {
  const apiFetch = useProgramApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      title: string;
      plannedOrder?: number;
      plannedWeek?: string | null;
      plannedLessonLabel?: string | null;
      plannedContent?: string | null;
      outcomeOrContentDotPoint?: string | null;
      plannedResources?: string | null;
      teacherNotes?: string | null;
    }) =>
      apiFetch<{ item: ProgramItem }>(
        `/api/program-registers/${registerId}/items`,
        { method: "POST", body: JSON.stringify(data) }
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: programRegisterDetailKey(registerId) }),
  });
}

export function useBulkCreateItems(registerId: number) {
  const apiFetch = useProgramApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      items: Array<{
        title: string;
        plannedOrder?: number;
        plannedWeek?: string | null;
        plannedLessonLabel?: string | null;
        plannedContent?: string | null;
        outcomeOrContentDotPoint?: string | null;
        plannedResources?: string | null;
        plannedAssessmentCheckpoint?: string | null;
      }>
    ) =>
      apiFetch<{ items: ProgramItem[] }>(
        `/api/program-registers/${registerId}/items/bulk`,
        { method: "POST", body: JSON.stringify({ items }) }
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: programRegisterDetailKey(registerId) }),
  });
}

export interface AiDraftItem {
  title: string;
  plannedWeek?: string | null;
  plannedLessonLabel?: string | null;
  plannedContent?: string | null;
  outcomeOrContentDotPoint?: string | null;
  plannedResources?: string | null;
  plannedAssessmentCheckpoint?: string | null;
  parsingNote?: string | null;
}

export interface AiDraftSection {
  kind: string;
  label: string;
}

export interface AiDraftResult {
  items: AiDraftItem[];
  sections?: AiDraftSection[];
  teachingSequenceFound?: boolean;
  hasUncertainSections?: boolean;
}

export function useAiParseItems(registerId: number) {
  const apiFetch = useProgramApi();
  return useMutation({
    mutationFn: (rawText: string) =>
      apiFetch<AiDraftResult>(
        `/api/program-registers/${registerId}/ai-draft`,
        { method: "POST", body: JSON.stringify({ rawText }) }
      ),
  });
}

export function useUpdateItem(registerId: number) {
  const apiFetch = useProgramApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      data,
    }: {
      itemId: number;
      data: Partial<{
        title: string;
        plannedWeek: string | null;
        plannedLessonLabel: string | null;
        plannedContent: string | null;
        outcomeOrContentDotPoint: string | null;
        plannedResources: string | null;
        teacherNotes: string | null;
        plannedOrder: number;
        overallStatus: string;
      }>;
    }) =>
      apiFetch<{ item: ProgramItem }>(
        `/api/program-registers/${registerId}/items/${itemId}`,
        { method: "PUT", body: JSON.stringify(data) }
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: programRegisterDetailKey(registerId) }),
  });
}

export function useDeleteItem(registerId: number) {
  const apiFetch = useProgramApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: number) =>
      apiFetch<{ ok: boolean }>(
        `/api/program-registers/${registerId}/items/${itemId}`,
        { method: "DELETE" }
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: programRegisterDetailKey(registerId) }),
  });
}

// ── Shared type for delivery event create/update payload ─────────────────────

type DeliveryEventPayload = {
  sourceType: "manual_entry" | "diary_entry";
  diaryEntryId?: number | null;
  classId?: number | null;
  deliveryDate: string;
  deliveryStatus: string;
  reflectionNote?: string | null;
  actualContentDelivered?: string | null;
  differentiationSupportTypes?: string[] | null;
  remainingContentNote?: string | null;
  manualSessionLabel?: string | null;
  period?: string | null;
};

type DeliveryEventUpdatePayload = Partial<DeliveryEventPayload>;

export function useCreateDeliveryEvent(registerId: number, itemId: number) {
  const apiFetch = useProgramApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: DeliveryEventPayload) =>
      apiFetch<{ event: ProgramDeliveryEvent }>(
        `/api/program-registers/${registerId}/items/${itemId}/delivery-events`,
        { method: "POST", body: JSON.stringify(data) }
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: programRegisterDetailKey(registerId) });
      // Invalidate diary-program-links so the Delivery Status button reflects
      // the new existingEvent without requiring a full page refresh.
      if (vars.diaryEntryId != null) {
        qc.invalidateQueries({ queryKey: diaryProgramLinksKey(vars.diaryEntryId) });
      }
    },
  });
}

export function useUpdateDeliveryEvent(
  registerId: number,
  eventId: number,
  diaryEntryId?: number | null,
) {
  const apiFetch = useProgramApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: DeliveryEventUpdatePayload) =>
      apiFetch<{ event: ProgramDeliveryEvent }>(
        `/api/program-registers/${registerId}/delivery-events/${eventId}`,
        { method: "PUT", body: JSON.stringify(data) }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: programRegisterDetailKey(registerId) });
      if (diaryEntryId != null) {
        qc.invalidateQueries({ queryKey: diaryProgramLinksKey(diaryEntryId) });
      }
    },
  });
}

// ── Diary search (for diary-entry picker in delivery event form) ──────────────

export interface DiarySearchEntry {
  id: number;
  date: string;
  slotKey: string;
  timetableId: number;
  courseId: number | null;
  courseName: string | null;
  contentPreview: string;
  contentForAutofill: string;
  dayNotes: string | null;
}

export function useDiarySearch(params: {
  q: string;
  startDate: string;
  endDate: string;
  courseId?: number | null;
  enabled: boolean;
}) {
  const apiFetch = useProgramApi();
  return useQuery({
    queryKey: ["diary-search", params.q, params.startDate, params.endDate, params.courseId],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (params.q) qs.set("q", params.q);
      qs.set("startDate", params.startDate);
      qs.set("endDate", params.endDate);
      if (params.courseId != null) qs.set("courseId", String(params.courseId));
      qs.set("limit", "30");
      return apiFetch<{ entries: DiarySearchEntry[] }>(`/api/diary/search?${qs.toString()}`);
    },
    enabled: params.enabled,
    staleTime: 30_000,
  });
}

export function useReorderItems(registerId: number) {
  const apiFetch = useProgramApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: number[]) =>
      apiFetch<{ ok: boolean }>(
        `/api/program-registers/${registerId}/items/reorder`,
        { method: "POST", body: JSON.stringify({ orderedIds }) }
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: programRegisterDetailKey(registerId) }),
  });
}

export function useDeleteDeliveryEvent(registerId: number) {
  const apiFetch = useProgramApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: number) =>
      apiFetch<{ ok: boolean }>(
        `/api/program-registers/${registerId}/delivery-events/${eventId}`,
        { method: "DELETE" }
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: programRegisterDetailKey(registerId) }),
  });
}

// ── Stage 3A2 — source file upload + extraction ───────────────────────────────

export interface ExtractionResult {
  structuredText: string;
  charCount: number;
  tableWarning: boolean;
  fileName: string;
}

/**
 * Patch sourceFileObjectKey + sourceFileName onto an existing register.
 * Called after the presigned-URL upload to GCS succeeds.
 */
export function useUpdateRegisterSourceFile(registerId: number) {
  const apiFetch = useProgramApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      sourceFileObjectKey: string;
      sourceFileName: string;
    }) =>
      apiFetch<{ register: ProgramRegister }>(
        `/api/program-registers/${registerId}`,
        { method: "PUT", body: JSON.stringify(data) }
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: programRegisterDetailKey(registerId) }),
  });
}

/**
 * Trigger server-side extraction of the register's uploaded source file.
 * The server uses register.sourceFileObjectKey from the DB (not client-supplied)
 * so only the file the teacher already uploaded is ever extracted.
 */
export function useExtractSourceFile(registerId: number) {
  const apiFetch = useProgramApi();
  return useMutation({
    mutationFn: () =>
      apiFetch<ExtractionResult>(
        `/api/program-registers/${registerId}/extract-source-file`,
        { method: "POST" }
      ),
  });
}

// ── Overall Evaluation ────────────────────────────────────────────────────────

export function useUpsertEvaluation(registerId: number) {
  const apiFetch = useProgramApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      overallReflection?: string | null;
      whatWorkedWell?: string | null;
      whatChangedFromPlannedProgram?: string | null;
      differentiationSupportSummary?: string | null;
      suggestedUpdatesForNextTime?: string | null;
      aiDraftUsed?: boolean;
      teacherEdited?: boolean;
    }) =>
      apiFetch<{ evaluation: ProgramEvaluation }>(
        `/api/program-registers/${registerId}/evaluation`,
        { method: "PUT", body: JSON.stringify(data) }
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: programRegisterDetailKey(registerId) }),
  });
}

export interface AiDraftEvaluationResult {
  overallReflection: string;
  whatWorkedWell: string;
  whatChangedFromPlannedProgram: string;
  differentiationSupportSummary: string;
  suggestedUpdatesForNextTime: string;
}

export function useAiDraftEvaluation(registerId: number) {
  const apiFetch = useProgramApi();
  return useMutation({
    mutationFn: () =>
      apiFetch<AiDraftEvaluationResult>(
        `/api/program-registers/${registerId}/ai-draft-evaluation`,
        { method: "POST" }
      ),
  });
}

// ── Diary program links ────────────────────────────────────────────────────
//
// Persists "Pull from Program" selections across page refreshes.
// Links are keyed to diary_entry_id + program_item_id (unique per slot).

export interface DiaryProgramLink {
  id: number;
  userId: string;
  diaryEntryId: number;
  programRegisterId: number;
  programItemId: number;
  createdAt: string;
  item: ProgramItem;
  existingEvent: ProgramDeliveryEvent | null;
  className: string | null;
}

export const diaryProgramLinksKey = (entryId: number) =>
  ["diary-program-links", entryId] as const;

export function useDiaryProgramLinks(entryId: number | null) {
  const apiFetch = useProgramApi();
  const { isLoaded } = useAuth();
  return useQuery({
    queryKey: diaryProgramLinksKey(entryId ?? 0),
    queryFn: () =>
      apiFetch<{ links: DiaryProgramLink[] }>(
        `/api/diary-entries/${entryId}/program-links`
      ),
    select: (data) => data.links,
    enabled: isLoaded && entryId !== null && entryId > 0,
  });
}

export function useAddDiaryProgramLink() {
  const apiFetch = useProgramApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      diaryEntryId,
      programRegisterId,
      programItemId,
    }: {
      diaryEntryId: number;
      programRegisterId: number;
      programItemId: number;
    }) =>
      apiFetch<{ link: DiaryProgramLink }>(
        `/api/diary-entries/${diaryEntryId}/program-links`,
        {
          method: "POST",
          body: JSON.stringify({ programRegisterId, programItemId }),
        }
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: diaryProgramLinksKey(vars.diaryEntryId),
      });
    },
  });
}
